import React, { useCallback } from 'react';
import { Surface } from '../layout/Surface';
import { useDraftDock } from '../../context/DraftDockContext';

/**
 * A place in a route's own layout for the scratchpad to live.
 *
 * On the project console the draft is not a thing floating over the dashboard —
 * it *is* one of the panes, taking the signals column for as long as it's open.
 * A note you're writing on Home is the thing you're doing there, so it gets the
 * same standing as the widgets rather than covering them up.
 *
 * ── Why a portal and not a second editor ─────────────────────────────────────
 * This renders the tile and an empty box; the dock portals its toolbar and
 * editor into that box. Rendering a second copy of the editor here would mean
 * two components racing the same autosave, and walking from Home to Tasks would
 * tear down one and mount the other — losing the caret, the block you were in,
 * and any write still sitting in the debounce. Through a portal the dock never
 * moves in the React tree; only its DOM parent does.
 *
 * The registration is a callback ref rather than an effect so it lands in the
 * same commit as the mount. An effect would leave one painted frame where the
 * pane exists and the dock still thinks it should float — the panel would flash
 * in the corner on the way in, and again on the way out.
 */
export default function DraftInlineSlot() {
    const dock = useDraftDock();
    const setInlineSlot = dock?.setInlineSlot;

    const ref = useCallback((el) => setInlineSlot?.(el), [setInlineSlot]);

    return (
        <Surface variant="tile" className="h-full" data-testid="draft-pane">
            {/* `empty:` covers the one frame before the lazy editor chunk lands
                on a cold cache — a faint wash rather than a hole in the console. */}
            <div
                ref={ref}
                className="flex min-h-0 flex-1 flex-col empty:animate-pulse empty:bg-white/[0.02]"
            />
        </Surface>
    );
}
